<template>
  <div ref="root" class="observer">
    <slot />
  </div>
</template>

<script>
import { defineComponent, onBeforeUnmount, onMounted, ref } from 'vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates.
   * @example
   * <ak-intersection-observer @intersect="fetchMore" />
   */
  name: 'AkIntersectionObserver',

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Object}
     * @description Options for the IntersectionObserver
     */
    options: {
      default: () => ({
        threshold: 0,
      }),
      type: Object,
    },
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['intersect', 'nonintersect'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const observer = ref(null)
    const observerProps = ref({})
    const root = ref(null)

    onMounted(() => {
      observer.value = new IntersectionObserver(([entry]) => {
        observerProps.value = entry
        if (entry && entry.isIntersecting) {
          emit('intersect', entry)
        } else if (entry && !entry.isIntersecting) {
          emit('nonintersect', entry)
        }
      }, props.options)

      observer.value.observe(root.value)
    })

    onBeforeUnmount(() => {
      try {
        observer.value.disconnect()
      } catch (ex) {
        /*istanbul ignore next */
      }
    })

    return {
      observerProps,
      observer,
      root,
    }
  },
})
</script>
